import imageUploadLogo from "../../assets/img/Upload-Photo-caregiver.svg";
import { ApiCaregiver } from "../../core/apis/caregiver";
import { AppRouteApi, AppRouteUi } from "../../config/appRoute";
import { Button } from "../../components/button.component";
import { Form, Toggle } from "rsuite";
import { useHistory } from "react-router";
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { UploadedFileType } from "../../models/forms/uploadFileType";
import {
  DoesObjectHasAnyInvalidValue,
  generateMultipartFormData,
} from "../../utils/formUtils";
import { CaregiverFormSchema } from "../../models/forms/caregiverForm";
import {
  TextInput,
  FileInput,
  SelectPickerInput,
  DateInput,
  NumberInput,
  TagInputWithIcon,
} from "../../components/textInput.component";
import { UtilityDate } from "../../utils/dateUtils";
import { ActionsCaregiverTable } from "../../core/redux/slices/sliceCaregiverTable";
import { useAppDispatch } from "../../core/redux/ReduxStore";
import { StorageAuth } from "../../core/localStorage";
import { PasswordInput } from "../../components/textInput.component";
import { Language } from "../../utils/language";
import { SpinLoader } from "../../components/spinLoader.component";
import { LocationSearchInput } from "../../components/locationSearchInput.component";
import { AddCertificateOnCaregiver } from "../../components/addCertificateOnCaregiver";

type CaregiverFormData = {
  DateOfBirth: Date;
  FirstName: string;
  Gender: string;
  LastName: string;
  PhoneNumber: string;
  ProfilePic: UploadedFileType[];
  Rate: string;
  email: string;
  SSN: string;
  ChampsIdNo: string;
  Certificates: UploadedFileType[];
  Address: string;
  CompanyId: string;
  UserName: string;
  Languages: string[];
  AvailableFrom: Date;
  AvailableTo: Date;
  WeeklyAvailabilities: string[];
  ApartmentNumber: string;
};

interface CertificateInterface {
  certificateFile?: UploadedFileType[];
  expiationDate?: Date;
}

export const UpdateCaregiver = ({
  showAvailableDays,
}: {
  showAvailableDays: boolean;
}) => {
  const routeHistory = useHistory();
  const firstLoadRef = useRef(false);
  const [autoClockCheckIn, setAutoClockCheckIn] = useState(false);
  const { caregiverId } = useParams<{ caregiverId: string }>();
  const { data, isValidating } = ApiCaregiver.ById.Hook(caregiverId);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isGPSCheckedIn, setIsGPSCheckedIn] = useState(false);
  const [isTransportAvailable, setIsTransportAvailable] = useState(false);
  const [isNotificationOn, setIsNotificationOn] = useState(false);
  const [certificates, setCertificates] = useState<CertificateInterface[]>([]);
  const [editableTimePermission, setEditableTimePermission] = useState(false);

  const genders = ["Male", "Female", "Others"].map((gender) => ({
    label: gender,
    value: gender,
  }));

  const availableDays = [
    "Saturday",
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
  ].map((day) => ({
    label: day,
    value: day,
  }));

  const [formData, setFormData] = useState<Partial<CaregiverFormData>>({
    DateOfBirth: new Date(),
    UserName: "",
    FirstName: "",
    Gender: "",
    LastName: "",
    PhoneNumber: "",
    ProfilePic: [],
    Rate: "",
    email: "",
    SSN: "",
    ChampsIdNo: "",
    Certificates: [],
    Address: "",
    Languages: [],
    AvailableFrom: new Date(),
    AvailableTo: new Date(),
    WeeklyAvailabilities: [],
    ApartmentNumber: "",
  });

  useEffect(() => {
    setFormData({
      DateOfBirth: new Date(data?.dateOfBirth || Date()),
      FirstName: data?.firstName,
      LastName: data?.lastName,
      Gender: `${data?.gender}`,
      PhoneNumber: data?.phoneNumber,
      Rate: `${data?.rate}`,
      email: data?.email,
      SSN: data?.ssn,
      ChampsIdNo: data?.champsIdNo,
      Address: data?.address,
      UserName: data?.userName,
      Languages: data?.languages,
      AvailableFrom: new Date(data?.availableFrom || Date()),
      AvailableTo: new Date(data?.availableTo || Date()),
      WeeklyAvailabilities: data?.weeklyAvailabilities ?? [],
      ApartmentNumber: data?.apartmentNumber,
    });
    if (data?.autoClockCheckIn) {
      setAutoClockCheckIn(true);
    }
    setIsTransportAvailable(!!data?.isTransportAvailable);
    setIsGPSCheckedIn(!!data?.gpsCheckInTurnedOn);
    setIsNotificationOn(!!data?.isNotificationEnabled);
    setEditableTimePermission(!!data?.editableTimePermission);
    convertToSSNFormat(data?.ssn ? data?.ssn : "");
    convertToPhoneNumberFormat(
      data?.phoneNumber ? data?.phoneNumber : "",
      "PhoneNumber"
    );
  }, [data]);

  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState({});
  const [isDisabled, setIsDisabled] = useState(false);
  const dispatch = useAppDispatch();
  const [isAddressTouched, setIsAddressTouched] = useState(false);
  const [isAvailableForWholeDay, setIsAvailableForWholeDay] = useState(false);
  const { CompanyId } = StorageAuth;

  useEffect(() => {
    const { ApartmentNumber, ...rest } = formData;
    const isEmpty = Object.values({ ...rest }).some(
      DoesObjectHasAnyInvalidValue
    );
    const errorCount = Object.keys(formError).length;
    const requiredFilled =
      !!formData.AvailableFrom &&
      !!formData.AvailableTo &&
      !!formData.DateOfBirth;
    var isDisabled =
      isEmpty ||
      !!errorCount ||
      !requiredFilled ||
      (showAvailableDays &&
        (!formData.WeeklyAvailabilities ||
          formData.WeeklyAvailabilities?.length === 0));
    setIsDisabled(isDisabled);
  }, [formData, formError, showAvailableDays]);

  const handleChange = useCallback((data: any, _: any) => {
    delete data.StartDate;
    delete data.EndDate;
    setFormData((prevFormData) => ({ ...prevFormData, ...data }));
  }, []);

  const setAvailableForWholeDay = () => {
    setIsAvailableForWholeDay(!isAvailableForWholeDay);
    if (!isAvailableForWholeDay) {
      const date = new Date();
      formData.AvailableFrom = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        0,
        0,
        0
      );
      formData.AvailableTo = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        23,
        59,
        59
      );
    } else {
      formData.AvailableFrom = new Date(data?.availableFrom || Date());
      formData.AvailableTo = new Date(data?.availableTo || Date());
    }
  };

  const convertToPhoneNumberFormat = (
    phoneNumber: string,
    propertyName: string
  ) => {
    phoneNumber = phoneNumber.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength > 3 && phoneNumberLength < 7) {
      phoneNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumberLength >= 7) {
      phoneNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6
      )}-${phoneNumber.slice(6, 10)}`;
    }

    setFormData((prev) => ({ ...prev, [propertyName]: phoneNumber }));
  };

  const convertToSSNFormat = (ssn: string) => {
    ssn = ssn.replace(/[^\d]/g, "");
    const ssnLength = ssn.length;

    if (ssnLength > 3 && ssnLength < 6) {
      ssn = `${ssn.slice(0, 3)}-${ssn.slice(3)}`;
    } else if (ssnLength >= 6) {
      ssn = `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5, 9)}`;
    }

    setFormData((prev) => ({ ...prev, SSN: ssn }));
  };

  const handleSubmit = async (
    checked: boolean,
    event: React.FormEvent<HTMLFormElement>
  ) => {
    if (!checked) return;
    formData.SSN = formData.SSN?.replace(/[-]/g, "");
    setIsLoading(true);
    const multipartFormData = generateMultipartFormData(formData);

    multipartFormData.append("SKey", caregiverId);

    certificates.forEach((cert, index) => {
      if (cert.certificateFile && cert.expiationDate) {
        multipartFormData.append(
          `Certificates[${index}].CertificateFile`,
          cert.certificateFile[0].blobFile
        );
        multipartFormData.append(
          `Certificates[${index}].ExpirationTime`,
          cert.expiationDate.toISOString()
        );
      }
    });

    multipartFormData.append(
      "IsTransportAvailable",
      isTransportAvailable ? "true" : "false"
    );
    multipartFormData.append(
      "GpsCheckInTurnedOn",
      isGPSCheckedIn ? "true" : "false"
    );
    multipartFormData.append("IsNotificationEnabled", `${isNotificationOn}`);
    multipartFormData.append("CompanyId", CompanyId || data?.companyId || "");
    multipartFormData.append("EditableTimePermission", `${editableTimePermission}`);

    if (newPassword && newPassword === confirmPassword) {
      multipartFormData.append("NewPassword", newPassword);
      multipartFormData.append("ConfirmNewPassword", confirmPassword);
    }

    removeCertificates.forEach((doc) =>
      multipartFormData.append("RemoveCertificates", doc)
    );

    if (autoClockCheckIn) {
      multipartFormData.append("AutoClockCheckIn", "true");
    } else {
      multipartFormData.append("AutoClockCheckIn", "false");
    }

    const result = await ApiCaregiver.Update(multipartFormData);
    if (result.isSuccess) {
      dispatch(ActionsCaregiverTable.goToPage(1));
      routeHistory.length > 1
        ? routeHistory.goBack()
        : routeHistory.push(AppRouteUi.Root);
    }

    setIsLoading(false);
  };

  const getTimeChangeHandler = (name: string) => {
    return (time: Date) => {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: time,
      }));
    };
  };

  const [removeCertificates, setRemoveCertificates] = useState<string[]>([]);

  const handleCertificateClick = (doc: string) => {
    removeCertificates.includes(doc)
      ? setRemoveCertificates(
          removeCertificates.filter((existing) => existing !== doc)
        )
      : setRemoveCertificates([...removeCertificates, doc]);
  };

  useEffect(() => {
    if (isValidating || !data || firstLoadRef.current) {
      return;
    }
    const fromDate = new Date(data.availableFrom);
    const toDate = new Date(data.availableTo);
    const secondDifference = (Number(toDate) - Number(fromDate)) / 1000;

    if (secondDifference === 86399) {
      setIsAvailableForWholeDay(true);
    }

    return () => {
      firstLoadRef.current = true;
    };
  }, [data, isValidating]);

  return (
    <>
      {isValidating ? (
        <SpinLoader />
      ) : (
        <Form
          className="row"
          onChange={handleChange}
          onSubmit={handleSubmit}
          formValue={formData}
          onCheck={setFormError}
          model={CaregiverFormSchema}
          fluid
        >
          <div className="col-md-10 col-12 border-end px-4 add-page-ctl">
            <div className="row">
              <TextInput name="FirstName" required>
                First Name
              </TextInput>
              <TextInput name="LastName" required>
                Last Name
              </TextInput>
            </div>

            <div className="row">
              <TextInput name="UserName" required fullWidth>
                User Name
              </TextInput>
              {/* <PasswordInput
                value={currentPassword}
                onChange={setCurrentPassword}
                name="currentPassword"
              >
                Current Password
              </PasswordInput> */}
            </div>

            {/* {currentPassword && ( */}
            <div className="row">
              <PasswordInput
                value={newPassword}
                onChange={setNewPassword}
                name="newPassword"
              >
                New Password
              </PasswordInput>
              <div className="col-md-6 px-1">
                <PasswordInput
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  name="confirmPassword"
                  fullWidth
                >
                  Confirm Password
                </PasswordInput>
                {newPassword &&
                  confirmPassword &&
                  newPassword !== confirmPassword && (
                    <p className="text-red-300">Password did not match</p>
                  )}
              </div>
            </div>

            <div className="row">
              <SelectPickerInput
                data={genders}
                name="Gender"
                required
                searchable={false}
                renderEmpty={
                  <p className="text-muted p-3">No Data Available</p>
                }
              >
                Gender
              </SelectPickerInput>
              <DateInput
                name="DateOfBirth"
                ranges={[]}
                disabledDate={UtilityDate.isDateAfterToday}
                required
              >
                Date of birth
              </DateInput>
            </div>

            <div className="row">
              <TextInput
                name="PhoneNumber"
                required
                value={formData.PhoneNumber}
                onChange={(val) =>
                  convertToPhoneNumberFormat(val, "PhoneNumber")
                }
              >
                Phone Number
              </TextInput>
              <TextInput disabled name="email" required>
                Email
              </TextInput>
            </div>

            <div className="row">
              <TextInput
                name="SSN"
                required
                onChange={(val) => convertToSSNFormat(val)}
              >
                Social security number
              </TextInput>
              <NumberInput name="ChampsIdNo" required>
                Champ's id number
              </NumberInput>
            </div>

            <div className="row">
              <NumberInput name="Rate" required>
                Caregiver's rate
              </NumberInput>
              {showAvailableDays && (
                <TagInputWithIcon
                  name="WeeklyAvailabilities"
                  data={availableDays}
                  required
                  value={formData.WeeklyAvailabilities}
                  placeholder="Select Available Days"
                  renderEmpty={
                    <p className="text-muted p-3">No Data Available</p>
                  }
                >
                  Available Days
                </TagInputWithIcon>
              )}
            </div>

            <div className="row">
              <Form.Group className="col-md-6 px-1">
                <Form.ControlLabel>
                  Address <span className="text-red-300">*</span>
                </Form.ControlLabel>
                <LocationSearchInput
                  onSelect={(address) => {
                    setFormData({ ...formData, Address: address });
                  }}
                  onBlur={() => setIsAddressTouched(true)}
                  value={formData.Address}
                />
                {isAddressTouched && !formData.Address && (
                  <small className="text-red-300 mb-2">
                    Please select a valid address
                  </small>
                )}
              </Form.Group>
              <TextInput name="ApartmentNumber">Apartment</TextInput>
            </div>
            <div className="row">
              <div className="col-md-3 align-items-center px-1 mb-3">
                Available for 24 hours
                <Toggle
                  onChange={() => setAvailableForWholeDay()}
                  checked={isAvailableForWholeDay}
                  size="sm"
                  className="ms-2"
                />
              </div>
            </div>

            <div className="row">
              <DateInput
                name="AvailableFrom"
                time
                timeChange={getTimeChangeHandler("AvailableFrom")}
                disabled={isAvailableForWholeDay}
                required
              >
                Available From
              </DateInput>
              <DateInput
                name="AvailableTo"
                time
                timeChange={getTimeChangeHandler("AvailableTo")}
                disabled={isAvailableForWholeDay}
                required
              >
                Available To
              </DateInput>
            </div>

            <div className="row">
              <TagInputWithIcon
                name="Languages"
                data={Language.getLanguageSelectItems()}
                required
                renderEmpty={
                  <p className="text-muted p-3">No Data Available</p>
                }
              >
                Languages
              </TagInputWithIcon>
            </div>

            <div className="row">
              <div className="col-md-3 align-items-center px-1 mb-3">
                Transportation Availability
                <Toggle
                  onChange={() =>
                    setIsTransportAvailable(!isTransportAvailable)
                  }
                  checked={isTransportAvailable}
                  size="sm"
                  className="ms-2"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-3 align-items-center px-1 mb-3">
                GPS
                <Toggle
                  onChange={() => setIsGPSCheckedIn(!isGPSCheckedIn)}
                  checked={isGPSCheckedIn}
                  size="sm"
                  className="ms-2"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-3 align-items-center px-1 mb-3">
                Auto Clock{" "}
                <Toggle
                  onChange={() => setAutoClockCheckIn(!autoClockCheckIn)}
                  checked={autoClockCheckIn}
                  size="sm"
                  className="ms-2"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 align-items-center px-1 mb-3">
                Notification
                <Toggle
                  onChange={() => setIsNotificationOn(!isNotificationOn)}
                  checked={isNotificationOn}
                  size="sm"
                  className="ms-2"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-3 align-items-center px-1 mb-3">
              Manage Clock In/Out
                <Toggle
                  onChange={() => setEditableTimePermission(!editableTimePermission)}
                  checked={editableTimePermission}
                  size="sm"
                  className="ms-2"
                />
              </div>
            </div>

            <div className="row">
              <FileInput
                listType="picture"
                imageSrc={imageUploadLogo}
                name="ProfilePic"
                accept="image/*"
                imageSrcApi={
                  data?.profilePic
                    ? AppRouteApi.Media.ByPath(data?.profilePic || "")
                    : imageUploadLogo
                }
              >
                Upload Photo
              </FileInput>
            </div>
            <div className="row px-1">
              <AddCertificateOnCaregiver
                onChange={setCertificates}
                value={certificates}
                uploadedCertificates={data?.certificates}
                handleRemove={handleCertificateClick}
                removeCertificates={removeCertificates}
              />
            </div>
          </div>

          <div className="col-md-2 col-12">
            <Button
              className="m-1 w-100 btn-green"
              type="submit"
              name="Save"
              loading={isLoading}
              disabled={isDisabled}
            />
            <Button
              onClick={() => {
                routeHistory.length > 1
                  ? routeHistory.goBack()
                  : routeHistory.push(AppRouteUi.Root);
              }}
              type="button"
              className="m-1 w-100 btn-green-subtle"
              disabled={isLoading}
              name="Return without saving"
            />
          </div>
        </Form>
      )}
    </>
  );
};
